import * as http from "api/http";
import { handleError, SERVICES_URL as servicesUrl } from "api/helper";
import { OfficeMergeTypes } from "types/configServiceModels";

export type PaymentMethodTypes = "CreditCard" | "Invoice" | "" | undefined;

const SERVICES_URL = servicesUrl;

export const { get, post, del, put } = http;

export const baseUrl = "config/auth/v1";
export const baseUrlV2 = "config/auth/v2";
export const basePublicUrl = "config/public/v1";

export const hqBaseUrl = `${baseUrl}/hq`;
export const hqBaseUrlV2 = `${baseUrlV2}/hq`;
export const hqSettingsBaseUrl = `${baseUrl}/hq-settings`;
export const officeSettingsBaseUrl = `${baseUrl}/office-settings`;

export const currentUserBaseUrl = `${baseUrl}/current-user`;
export const currentUserBaseUrlV2 = `${baseUrlV2}/current-user`;
export const officeBaseUrl = `${baseUrl}/office`;
import { ConfigurationService } from "@adfenix/types";
import { useQuery } from "@tanstack/react-query";

export const getHqs = (): Promise<ConfigurationService.HqConfig[]> =>
  get(`${SERVICES_URL}/${hqBaseUrl}`).catch(handleError);

export const getHqById = (
  hqId: string
): Promise<ConfigurationService.HqConfig> =>
  get(`${SERVICES_URL}/${hqBaseUrl}/${hqId}`).catch(handleError);

export const getOfficeById = (
  officeId: string
): Promise<ConfigurationService.OfficeConfig> =>
  get(`${SERVICES_URL}/${officeBaseUrl}/${officeId}`).catch(handleError);

export const getUserOffices = (): Promise<
  ConfigurationService.OfficeForTokenModel[]
> => get(`${SERVICES_URL}/${currentUserBaseUrl}/offices`).catch(handleError);

export const getOfficesByHqId = (
  hqId: string
): Promise<ConfigurationService.OfficeConfig[]> =>
  get(`${SERVICES_URL}/${hqBaseUrl}/${hqId}/offices`).catch(handleError);

export const getHqAndDomains = (): Promise<
  ConfigurationService.HqAndDomainModel[]
> =>
  get(`${SERVICES_URL}/${currentUserBaseUrl}/hq-and-domains`).catch(
    handleError
  );

export const useHqAndDomains = (userEmail: string) =>
  useQuery(["getHqAndDomains", userEmail], () => getHqAndDomains(), {
    enabled: !!userEmail
  });

export const getAttributesData = (
  hqId: string
): Promise<ConfigurationService.AttributesModel[]> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/attribute`).catch(
    handleError
  );

export const updateAttributesData = ({
  hqId,
  attribute
}: {
  hqId: string;
  attribute: ConfigurationService.AttributeModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/attribute`,
    attribute
  ).catch(handleError);

export const addAttribute = ({
  hqId,
  attribute
}: {
  hqId: string;
  attribute: Omit<ConfigurationService.AttributeModel, "id">;
}): Promise<void> =>
  post(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/attribute`,
    attribute
  ).catch(handleError);

export const deleteAttribute = ({
  hqId,
  attributeId,
  attributeType
}: {
  hqId?: string;
  attributeType: ConfigurationService.AttributeType;
  attributeId: string;
}): Promise<void> =>
  del(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/attribute/${attributeType}/${attributeId}`
  ).catch(handleError);

export const getDetectWebsiteData = (
  hqId: string
): Promise<ConfigurationService.PropertyDetector[]> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/detect-website`).catch(
    handleError
  );

export const getIdentifyWebsiteData = (
  hqId: string
): Promise<ConfigurationService.PropertyIdentifier[]> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/identify-website`).catch(
    handleError
  );

export const updateIdentifyWebsiteData = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.PropertyIdentifier;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/identify-website`,
    model
  ).catch(handleError);

export const addIdentifyWebsiteData = ({
  hqId,
  model
}: {
  hqId: string;
  model: Omit<ConfigurationService.PropertyIdentifier, "id">;
}): Promise<void> =>
  post(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/identify-website`,
    model
  ).catch(handleError);

export const deleteIdentifyWebsiteRow = ({
  id,
  hqId
}: {
  id: string;
  hqId?: string;
}): Promise<void> =>
  del(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/identify-website/${id}`
  ).catch(handleError);

export const getPropertyAttributesData = (
  hqId: string
): Promise<ConfigurationService.PropertyAttribute[]> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/property-attributes`).catch(
    handleError
  );

export const updatePropertyAttributesData = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.PropertyAttribute;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/property-attributes`,
    model
  ).catch(handleError);

export const addPropertyAttributesData = ({
  hqId,
  model
}: {
  hqId: string;
  model: Omit<ConfigurationService.PropertyAttribute, "id">;
}): Promise<void> =>
  post(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/property-attributes`,
    model
  ).catch(handleError);

export const deletePropertyAttributeRow = ({
  id,
  hqId
}: {
  id: string;
  hqId?: string;
}): Promise<void> =>
  del(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/property-attributes/${id}`
  ).catch(handleError);

export const getCleanupUrlData = (
  hqId: string
): Promise<ConfigurationService.PropertyUrlCleaner[]> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/cleanup-url`).catch(
    handleError
  );

export const updateCleanupUrlData = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.PropertyUrlCleaner;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/cleanup-url`, model).catch(
    handleError
  );

export const addCleanupUrlData = ({
  hqId,
  model
}: {
  hqId: string;
  model: Omit<ConfigurationService.PropertyUrlCleaner, "id">;
}): Promise<void> =>
  post(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/cleanup-url`, model).catch(
    handleError
  );

export const deleteCleanupUrlRow = ({
  hqId,
  id
}: {
  id: string;
  hqId?: string;
}): Promise<void> =>
  del(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/cleanup-url/${id}`).catch(
    handleError
  );

export const getAlert = (): Promise<{
  title: string;
  message: string;
  unixTimestamp: string;
}> => get(`${SERVICES_URL}/${basePublicUrl}/alert`).catch(handleError);

export const deleteAlert = (): Promise<void> =>
  del(`${SERVICES_URL}/${baseUrl}/alert`).catch(handleError);

export const updateAlert = ({
  title,
  message
}: {
  title: string;
  message: string;
}): Promise<void> =>
  put(`${SERVICES_URL}/${baseUrl}/alert`, {
    title,
    message
  }).catch(handleError);

export const getAccountSettingsData = (
  hqId: string
): Promise<ConfigurationService.HqAccountModel> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/account`).catch(
    handleError
  );

export const createNewHQ = ({
  name,
  descriptiveName,
  domain
}: ConfigurationService.NewHqModel): Promise<void> =>
  post(`${SERVICES_URL}/${hqSettingsBaseUrl}`, {
    name,
    descriptiveName,
    domain
  }).catch(handleError);

export const createNewOffice = ({
  hqId,
  name
}: {
  hqId: string;
  name: string;
}): Promise<void> =>
  post(`${SERVICES_URL}/${hqBaseUrl}/${hqId}/office/${name}`).catch(
    handleError
  );

export const updateGeneralHQInformation = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.GeneralInformationModel;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/general`, model).catch(
    handleError
  );

export const updateHQLocalization = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.LocalizationModel;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/localization`, model).catch(
    handleError
  );

export const updateHQDomains = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.DomainsModel;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/domains`, model).catch(
    handleError
  );

// Office settings endpoints
export const getOfficeSettingsData = (
  officeId: string
): Promise<ConfigurationService.OfficeAccountModel> =>
  get(`${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/account`).catch(
    handleError
  );

export const updateGeneralOfficeInformation = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.OfficeGeneralModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/general`,
    model
  ).catch(handleError);

export const updateOfficeBillingInformation = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.OfficeFeatureSettingsModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/billing`,
    model
  ).catch(handleError);

export const updateOfficeLegalDisclaimer = ({
  officeId,
  message
}: {
  officeId: string;
  message: ConfigurationService.LegalDisclaimerModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/legal-disclaimer`,

    message
  ).catch(handleError);

export const mergeOffices = ({
  officeToDelete,
  officeToKeep
}: OfficeMergeTypes): Promise<void> => //TO_DO set to ConfigurationService.OfficeMergeModel and fix conflicts
  post(`${SERVICES_URL}/${officeSettingsBaseUrl}/office-merge`, {
    officeToDelete,
    officeToKeep
  }).catch(handleError);

export const getHqChannels = (
  hqId: string
): Promise<ConfigurationService.HqChannelsModel> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/channels`).catch(
    handleError
  );

export const updateHqFacebookSettingsRequest = ({
  model,
  hqId
}: {
  hqId: string;
  model: ConfigurationService.DashboardConfigFacebook;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/facebook-channel`,
    model
  ).catch(handleError);

export const updateHqInstagramSettingsRequest = ({
  model,
  hqId
}: {
  hqId: string;
  model: ConfigurationService.DashboardConfigInstagram;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/instagram-channel`,
    model
  ).catch(handleError);

export const updateHqPriceSettingsRequest = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.PriceSettingsModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/price-settings`,
    model
  ).catch(handleError);

export const getOfficeChannels = (
  officeId: string
): Promise<ConfigurationService.OfficeChannelsModel> =>
  get(`${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/channels`).catch(
    handleError
  );

export const updateOfficeFacebookSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.OfficeChannelsModel["facebook"];
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/facebook-channel`,
    model
  ).catch(handleError);

export const updateOfficeDisplayAdSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.OfficeChannelsModel["displayAd"];
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/display-ad-channel`,
    model
  ).catch(handleError);

export const updateOfficeInstagramSettingsRequest = ({
  model,
  officeId
}: {
  officeId: string;
  model: ConfigurationService.OfficeDashboardConfigInstagramModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/instagram-channel`,
    model
  ).catch(handleError);

export const updateOfficePriceSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.OfficeChannelsModel["price"];
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/price-settings`,
    model
  ).catch(handleError);

export const updateHqDisplayAdSettingsRequest = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.DisplayAdSettingsModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/display-ad-channel`,
    model
  ).catch(handleError);

export const getHqIntegrations = (
  hqId: string
): Promise<ConfigurationService.HqIntegrationsModel> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/integrations`).catch(
    handleError
  );

export const updateHqFacebookIntegration = ({
  hqId,
  facebook
}: {
  hqId: string;
  facebook: ConfigurationService.FacebookSettingsModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/facebook-integrations`,
    facebook
  ).catch(handleError);

export const getHqPublishing = (
  hqId: string
): Promise<ConfigurationService.PublishingModel> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/publishing`).catch(
    handleError
  );

export const updateHqCopsNote = ({
  hqId,
  settings
}: {
  hqId: string;
  settings: ConfigurationService.CopsModel;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/cops-note`, settings).catch(
    handleError
  );

export const updateHqUpcoming = ({
  hqId,
  settings
}: {
  hqId: string;
  settings: ConfigurationService.UpcomingSettings;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/upcoming`, settings).catch(
    handleError
  );

export const updateHqPostSaleAgentBooster = ({
  hqId,
  settings
}: {
  hqId: string;
  settings: ConfigurationService.PostSaleAgentBoosterSettings;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/post-sale-agent-booster`,
    settings
  ).catch(handleError);

export const updateHqPreSaleAgentBooster = ({
  hqId,
  settings
}: {
  hqId: string;
  settings: ConfigurationService.PreSaleAgentBoosterSettingsModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/pre-sale-agent-booster`,
    settings
  ).catch(handleError);

export const updateHqReboost = ({
  hqId,
  settings
}: {
  hqId: string;
  settings: ConfigurationService.ReboostSettingsModel;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/reboost`, settings).catch(
    handleError
  );

export const delHqReboostRule = ({
  hqId,
  id
}: {
  id: string;
  hqId: string;
}): Promise<void> =>
  del(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/reboost-rule/${id}`).catch(
    handleError
  );

export const addHqReboostRule = ({
  hqId,
  rule
}: {
  rule: ConfigurationService.ReboostRule;
  hqId: string;
}): Promise<void> =>
  post(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/reboost-rule`, rule).catch(
    handleError
  );

export const updateHqReboostRule = ({
  hqId,
  rule
}: {
  rule: ConfigurationService.ReboostRule;
  hqId: string;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/reboost-rule`, rule).catch(
    handleError
  );

export const updateHqHomeBooster = ({
  hqId,
  settings
}: {
  hqId: string;
  settings: ConfigurationService.HomeBoosterSettingsModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/home-booster`,
    settings
  ).catch(handleError);

export const delHqAutopublishRule = ({
  hqId,
  id
}: {
  id: string;
  hqId: string;
}): Promise<void> =>
  del(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/auto-publish-rule/${id}`
  ).catch(handleError);

export const addHqAutopublishRule = ({
  hqId,
  rule
}: {
  rule: ConfigurationService.AutoPublishRule;
  hqId: string;
}): Promise<void> =>
  post(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/auto-publish-rule`,
    rule
  ).catch(handleError);

export const updateHqAutopublishRule = ({
  hqId,
  rule
}: {
  rule: ConfigurationService.AutoPublishRule;
  hqId: string;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/auto-publish-rule`,
    rule
  ).catch(handleError);

export const getOfficePublishingSettings = (
  officeId: string
): Promise<ConfigurationService.OfficePublishingModel> =>
  get(`${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/publishing`).catch(
    handleError
  );

export const updateOfficeHomeBoosterPublishingSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: { publishMode: ConfigurationService.HomeBoosterPublishMode };
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/home-booster`,
    model
  ).catch(handleError);

export const addOfficeAutoPublishRule = ({
  officeId,
  rule
}: {
  officeId: string;
  rule: ConfigurationService.AutoPublishRule;
}): Promise<void> =>
  post(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/auto-publish-rule`,
    rule
  ).catch(handleError);

export const updateOfficeAutoPublishRule = ({
  officeId,
  rule
}: {
  officeId: string;
  rule: ConfigurationService.AutoPublishRule;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/auto-publish-rule`,
    rule
  ).catch(handleError);

export const deleteOfficeAutoPublishRule = ({
  officeId,
  ruleId
}: {
  officeId?: string;
  ruleId: string;
}): Promise<void> =>
  del(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/auto-publish-rule/${ruleId}`
  ).catch(handleError);

export const updateOfficePreSaleAgentBoosterSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.OfficePreSaleAgentBoosterSettingsModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/pre-sale-agent-booster`,
    model
  ).catch(handleError);

export const updateOfficePostSaleAgentBoosterSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.PostSaleAgentBoosterSettings;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/post-sale-agent-booster`,
    model
  ).catch(handleError);

export const updateOfficeReboostSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: {
    publishMode: ConfigurationService.ReboostPublishMode;
  };
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/reboost`,
    model
  ).catch(handleError);

export const addOfficeReboostRule = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.ReboostRule;
}): Promise<void> =>
  post(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/reboost-rule`,
    model
  ).catch(handleError);

export const updateOfficeReboostRule = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.ReboostRule;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/reboost-rule`,
    model
  ).catch(handleError);

export const deleteOfficeReboostRule = ({
  officeId,
  ruleId
}: {
  officeId: string;
  ruleId: string;
}): Promise<void> =>
  del(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/reboost-rule/${ruleId}`
  ).catch(handleError);

export const updateOfficeUpcomingSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: {
    publishMode: ConfigurationService.UpcomingPublishMode;
  };
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/upcoming`,
    model
  ).catch(handleError);

export const updateOfficeCopsNote = ({
  officeId,
  model
}: {
  officeId: string;
  model: {
    note: ConfigurationService.CopsModel["note"];
  };
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/cops-note`,
    model
  ).catch(handleError);

export const getOfficeIntegrations = (
  officeId: string
): Promise<ConfigurationService.OfficeIntegrations> =>
  get(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/integrations`
  ).catch(handleError);

export const getUrlTemplateData = (
  hqId: string
): Promise<ConfigurationService.UrlTemplates> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/url-templates`).catch(
    handleError
  );

export const updateListingImageUrlTemplateData = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.UrlTemplate;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/url-templates/listing-image`,
    model
  ).catch(handleError);

export const updateMultipleListingImagesUrlTemplateData = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.UrlTemplate;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/url-templates/multiple-listing-images`,
    model
  ).catch(handleError);

export const updateAgentImageUrlTemplateData = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.UrlTemplate;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/url-templates/agent-image`,
    model
  ).catch(handleError);

export const updateAgentPageUrlTemplateData = ({
  hqId,
  model
}: {
  hqId: string;
  model: ConfigurationService.UrlTemplate;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/url-templates/agent-page`,
    model
  ).catch(handleError);

export const getEngageHqSettings = (
  hqId: string
): Promise<ConfigurationService.EngageSettingsModel> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/engage`).catch(handleError);

export const getEngageHQCaptureApps = (
  hqId: string
): Promise<ConfigurationService.EngageSettings> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/capture`).catch(
    handleError
  );

export const updateEngageHQCaptureApps = ({
  hqId,
  request
}: {
  hqId: string;
  request: ConfigurationService.EngageSettings;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/capture`, {
    ...request
  }).catch(handleError);

export const getEngageHQEnhanceEvents = (
  hqId: string
): Promise<ConfigurationService.EnhanceSettings> =>
  get(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/enhance`).catch(
    handleError
  );

export const updateEngageHQEnhanceEvents = ({
  hqId,
  request
}: {
  hqId: string;
  request: ConfigurationService.EnhanceSettings;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/enhance`, {
    ...request
  }).catch(handleError);

export const getEngageOfficeCaptureApps = (
  officeId: string
): Promise<ConfigurationService.OfficeEngageSettings> =>
  get(`${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/capture`).catch(
    handleError
  );

export const updateEngageOfficeCaptureApps = ({
  officeId,
  request
}: {
  officeId: string;
  request: ConfigurationService.OfficeEngageSettings;
}): Promise<void> =>
  put(`${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/capture`, {
    ...request
  }).catch(handleError);

export const copyEngageCaptureAppsToAllOffices = ({
  hqId,
  request
}: {
  hqId: string;
  request: ConfigurationService.OfficeEngageSettings;
}): Promise<void> =>
  post(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/capture/copy-to-offices`, {
    ...request
  }).catch(handleError);

export const getEngageOfficeEnhanceEvents = (
  officeId: string
): Promise<ConfigurationService.OfficeEnhanceSettings> =>
  get(`${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/enhance`).catch(
    handleError
  );

export const updateEngageOfficeEnhanceEvents = ({
  officeId,
  request
}: {
  officeId: string;
  request: ConfigurationService.OfficeEnhanceSettings;
}): Promise<void> =>
  put(`${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/enhance`, {
    ...request
  }).catch(handleError);

export const copyEngageEnhanceEventsToAllOffices = ({
  hqId,
  request
}: {
  hqId: string;
  request: ConfigurationService.OfficeEnhanceSettings;
}): Promise<void> =>
  post(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/enhance/copy-to-offices`, {
    ...request
  }).catch(handleError);

export const getIntegrations = ({
  hqId,
  officeId
}: {
  hqId: string;
  officeId: string;
}): Promise<ConfigurationService.ApplySettingsIntegrations> =>
  get(
    `${SERVICES_URL}/${baseUrl}/${hqId}/${officeId}/apply-settings/integrations`
  ).catch(handleError);

export const bulkActivateOffices = ({
  officeIds
}: {
  officeIds: string[];
}): Promise<string> =>
  put(`${SERVICES_URL}/${baseUrl}/office-settings/activate`, officeIds).catch(
    handleError
  );

export const bulkDeactivateOffices = ({
  officeIds
}: {
  officeIds: string[];
}): Promise<string> =>
  put(`${SERVICES_URL}/${baseUrl}/office-settings/deactivate`, officeIds).catch(
    handleError
  );

export const bulkDeleteOffices = ({
  officeIds
}: {
  officeIds: string[];
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseUrl}/office-settings/bulk-delete`,
    officeIds
  ).catch(handleError);

export const getIsHqMigrated = (hqId: string): Promise<boolean> =>
  get(`${SERVICES_URL}/${hqBaseUrl}/${hqId}/migrated-to-programs`).catch(
    handleError
  );

export const getFeatureToggle = (
  userId: string,
  name: string
): Promise<boolean> =>
  get(
    `${SERVICES_URL}/${baseUrl}/feature-toggle?userId=${userId}&name=${name}`
  ).catch(handleError);

export const updateOfficePaymentMethod = ({
  officeId,
  paymentMethodType
}: {
  officeId?: string;
  paymentMethodType: PaymentMethodTypes;
}): Promise<void> =>
  put(`${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/payment-method`, {
    paymentMethod: paymentMethodType
  }).catch(handleError);

export const updateHqPaymentMethod = ({
  hqId,
  paymentMethodType
}: {
  hqId?: string;
  paymentMethodType: PaymentMethodTypes;
}): Promise<void> =>
  put(`${SERVICES_URL}/${hqSettingsBaseUrl}/${hqId}/payment-method`, {
    paymentMethod: paymentMethodType
  }).catch(handleError);

export const updateOfficeCampaignSettings = ({
  officeId,
  model
}: {
  officeId: string;
  model: ConfigurationService.OfficeCampaignSettingsModel;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${officeSettingsBaseUrl}/${officeId}/campaign`,
    model
  ).catch(handleError);

export const bulkMergeOffices = (
  model: ConfigurationService.OfficesMergeModel
): Promise<void> =>
  post(`${SERVICES_URL}/${officeSettingsBaseUrl}/merge-offices`, model).catch(
    handleError
  );
